import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { fromJS, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import { getMembers, type GetMembersQueryParams } from '@gonfalon/openapi/operations/getMembers';

import { createMember, type Member } from '#utils/members/records/Member';

export type MemberAccessCheck = NonNullable<NonNullable<GetMembersQueryParams['filter']>['accessCheck']>;

type RequestOptions = { signal?: AbortSignal };

const ACCESS_CHECK_SCAN_PAGES = 5;

async function fetchMembersPage(query: GetMembersQueryParams, options: RequestOptions) {
  const { data, error, response } = await getMembers({ query }, options);
  if (!response.ok) {
    throw new RESTAPIError({
      status: response.status,
      url: response.url,
      code: 'unknown_error',
      message: response.statusText || `HTTP ${response.status}`,
      cause: error,
    });
  }
  return OrderedMap<string, Member>(
    (data?.items ?? []).map((item) => {
      const member = createMember(fromJS(item));
      return [member._id, member];
    }),
  );
}

function firstMembers(members: OrderedMap<string, Member>, count: number) {
  return OrderedMap<string, Member>(members.toArray().slice(0, count));
}

export async function searchMembers(
  { query, limit }: { query?: string; limit: number },
  options: RequestOptions = {},
): Promise<OrderedMap<string, Member>> {
  return fetchMembersPage({ filter: query ? { query } : undefined, limit, sort: 'displayName' }, options);
}

export async function narrowMembersByAccessCheck(
  members: OrderedMap<string, Member>,
  accessCheck: MemberAccessCheck,
  options: RequestOptions = {},
): Promise<OrderedMap<string, Member>> {
  if (members.isEmpty()) {
    return members;
  }
  const ids = members.keySeq().toArray();
  const allowed = await fetchMembersPage({ filter: { ids, accessCheck }, limit: ids.length }, options);
  return members.filter((_, id) => allowed.has(id));
}

export function searchMembersWithAccess(
  { query, pageSize, accessCheck }: { query?: string; pageSize: number; accessCheck?: MemberAccessCheck },
  options: RequestOptions = {},
): { members: Promise<OrderedMap<string, Member>>; membersWithAccess: Promise<OrderedMap<string, Member>> } {
  if (!accessCheck) {
    const members = searchMembers({ query, limit: pageSize }, options);
    return { members, membersWithAccess: members };
  }
  const candidates = searchMembers({ query, limit: pageSize * ACCESS_CHECK_SCAN_PAGES }, options);
  return {
    members: candidates.then((page) => firstMembers(page, pageSize)),
    membersWithAccess: candidates
      .then((page) => narrowMembersByAccessCheck(page, accessCheck, options))
      .then((allowed) => firstMembers(allowed, pageSize)),
  };
}
